// MobileOrders.jsx
// Phone-tier Orders — Positions / Trades / Orders in one scrollable view. A thin
// PRESENTER: all live data (windowed orders + positions + grouped trades) and the
// cancel handler come from Orders.jsx (the container) as props — nothing is
// re-fetched here (same container/presenter split as MobileWatchList /
// MobileAlgoDashboard). Layout:
//   • tab switcher (Positions · Trades · Orders) + 1D/1W/1M range
//   • Positions: side · qty · avg, trailing LTP + unrealized P&L (TickFlash)
//   • Trades: side · qty · entry · status, trailing realized P&L
//   • Orders: side · type · price · qty · time, status badge; swipe a cancellable
//     order → Cancel (confirm-gated, real cancelOrder)
import React, { useState } from "react";
import { ListRow, SwipeRow, Sheet, TickFlash, PullToRefresh } from "../ds";
import { getOrderWorkingPrice } from "../../utils/tradingTransforms";

const mono = { fontFamily: "var(--font-mono, ui-monospace, monospace)", fontVariantNumeric: "tabular-nums" };

const inr = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Math.round(n);
  return `${v < 0 ? "−" : "+"}₹${Math.abs(v).toLocaleString("en-IN")}`;
};
const price = (n) =>
  n == null || n === "" || isNaN(n)
    ? "—"
    : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ago(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (isNaN(s)) return "";
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const pnlColor = (n) => (n > 0 ? "var(--color-profit)" : n < 0 ? "var(--color-loss)" : "var(--fg-1)");

// status → DS badge variant (--badge-* tokens; matches the web StatusBadge intent)
const STATUS_VARIANT = {
  FILLED: "profit", PENDING: "warn", SUBMITTED: "info", OPEN: "info",
  PARTIALLY_FILLED: "warn", MONITORING: "info", REJECTED: "loss",
  CANCELLED: "neut", UNKNOWN: "neut",
};
const statusLabel = (s) => (s === "PARTIALLY_FILLED" ? "PARTIAL" : s || "—");
const CANCELLABLE = new Set(["PENDING", "SUBMITTED", "OPEN", "PARTIALLY_FILLED", "MONITORING"]);

const TABS = ["Positions", "Trades", "Orders"];
const RANGES = [{ v: "1D", l: "1D" }, { v: "1W", l: "1W" }, { v: "1M", l: "1M" }];

// Recessed segmented (neutral) — same pattern as MobileAlgoDashboard's ModeFilter.
function Segmented({ options, value, onChange, grow }) {
  return (
    <div style={{ display: grow ? "flex" : "inline-flex", gap: 4, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 10, padding: 3 }}>
      {options.map((o) => {
        const val = o.v ?? o;
        const label = o.l ?? o;
        const active = value === val;
        return (
          <button
            key={val}
            type="button"
            onClick={() => onChange?.(val)}
            style={{
              appearance: "none", cursor: "pointer", border: "none",
              flex: grow ? 1 : "0 0 auto",
              fontSize: 12.5, fontWeight: 500, padding: "6px 14px", borderRadius: 7,
              background: active ? "var(--bg-3)" : "transparent",
              color: active ? "var(--fg-1)" : "var(--fg-3)",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

const chip = { fontSize: 9, fontWeight: 600, color: "var(--fg-3)", border: "1px solid var(--border)", padding: "1px 5px", borderRadius: 3, letterSpacing: ".05em", flex: "0 0 auto" };
const badge = (variant) => `ds-badge xs ${variant}`;

function TitleLine({ symbol, exchange, tag, tagColor }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{symbol || "—"}</span>
      {exchange && <span style={chip}>{exchange}</span>}
      {tag && <span style={{ fontSize: 9, fontWeight: 700, color: tagColor, flex: "0 0 auto" }}>{tag}</span>}
    </span>
  );
}

const rowBorder = (last) => (last ? undefined : { borderBottom: "1px solid var(--border)" });

// ── Positions ────────────────────────────────────────────────────────────────
function PositionRow({ p, last }) {
  const side = p.side === "SHORT" || p.side === "SELL" ? "SHORT" : "LONG";
  const qty = p.qty ?? p.quantity ?? "";
  const avg = p.avg_entry_price ?? p.avg_cost ?? p.entry_price;
  const upnl = p.unrealized_pnl;
  return (
    <div style={rowBorder(last)}>
      <ListRow
        leading={<span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, flex: "0 0 auto", background: side === "LONG" ? "var(--color-profit)" : "var(--color-loss)" }} />}
        title={<TitleLine symbol={p.display_symbol || p.symbol || p.instrument_symbol || p.name} exchange={p.exchange} tag={side} tagColor={side === "LONG" ? "var(--color-profit)" : "var(--color-loss)"} />}
        subtitle={`${qty} @ ₹${price(avg)}`}
        trailing={
          <TickFlash value={upnl} sign="auto" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, borderRadius: 6, padding: "1px 4px", margin: "-1px -4px" }}>
            <span style={{ ...mono, fontSize: 14, fontWeight: 600, color: pnlColor(upnl) }}>{inr(upnl)}</span>
            <span style={{ ...mono, fontSize: 11, color: "var(--fg-3)" }}>{p.ltp != null ? price(p.ltp) : "—"}</span>
          </TickFlash>
        }
      />
    </div>
  );
}

// ── Trades ───────────────────────────────────────────────────────────────────
function TradeRow({ t, last }) {
  const long = t.side === "LONG";
  return (
    <div style={rowBorder(last)}>
      <ListRow
        leading={<span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, flex: "0 0 auto", background: long ? "var(--color-profit)" : "var(--color-loss)" }} />}
        title={<TitleLine symbol={t.display_symbol || t.symbol || t.name} tag={t.side} tagColor={long ? "var(--color-profit)" : "var(--color-loss)"} />}
        subtitle={`${t.qty ?? "—"} @ ₹${price(t.entryPrice)} · ${t.status === "open" ? "Open" : "Closed"}${t.entryTime ? " · " + ago(new Date(t.entryTime).toISOString()) : ""}`}
        trailing={
          <TickFlash value={t.pnl} sign="auto" style={{ display: "flex", alignItems: "center", borderRadius: 6, padding: "1px 4px", margin: "-1px -4px" }}>
            <span style={{ ...mono, fontSize: 14, fontWeight: 600, color: pnlColor(t.pnl) }}>{inr(t.pnl)}</span>
          </TickFlash>
        }
      />
    </div>
  );
}

// ── Orders ───────────────────────────────────────────────────────────────────
function OrderRow({ o, last, onCancelRequest }) {
  const buy = (o.side || "").toUpperCase() === "BUY";
  const type = o.type || o.order_type || "";
  const px = getOrderWorkingPrice(o);
  const filled = o.filled_price ?? o.filledPrice;
  const status = (o.status || "UNKNOWN").toUpperCase();
  const created = o.created_at || o.order_timestamp;
  const cancellable = CANCELLABLE.has(status) && !o.pending_corrective_type;

  const face = (
    <ListRow
      leading={<span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, flex: "0 0 auto", background: buy ? "var(--color-profit)" : "var(--color-loss)" }} />}
      title={<TitleLine symbol={o.display_symbol || o.symbol || o.name} exchange={o.exchange} tag={buy ? "BUY" : "SELL"} tagColor={buy ? "var(--color-profit)" : "var(--color-loss)"} />}
      subtitle={`${type}${px != null ? " ₹" + price(px) : ""} · ${o.qty ?? "—"} qty${created ? " · " + ago(created) : ""}`}
      trailing={
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
          <span className={badge(STATUS_VARIANT[status] || "neut")}>{statusLabel(status)}</span>
          {filled != null && <span style={{ ...mono, fontSize: 11, color: "var(--fg-3)" }}>₹{price(filled)}</span>}
        </span>
      }
    />
  );

  if (!cancellable) return <div style={rowBorder(last)}>{face}</div>;
  return (
    <SwipeRow style={rowBorder(last)} actions={[{ key: "cancel", label: "Cancel", tone: "danger", icon: CancelGlyph, onClick: () => onCancelRequest(o) }]}>
      {face}
    </SwipeRow>
  );
}

const CancelGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
);

function CancelConfirmSheet({ target, onCancel, onConfirm }) {
  const id = target?.order_id ?? target?.id;
  return (
    <Sheet open={!!target} onClose={onCancel} title="Cancel order">
      <p style={{ fontSize: 14, color: "var(--fg-2)", lineHeight: 1.5, margin: "0 0 4px" }}>
        Cancel this {(target?.side || "").toUpperCase()} order for <b style={{ color: "var(--fg-1)" }}>{target?.display_symbol || target?.symbol || target?.name || id}</b>? It will be cancelled at the broker — this can't be undone.
      </p>
      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <button type="button" onClick={onCancel} style={{ flex: 1, appearance: "none", cursor: "pointer", height: 46, borderRadius: 12, background: "var(--bg-3)", border: "1px solid var(--border)", color: "var(--fg-1)", fontSize: 14, fontWeight: 600 }}>
          Keep order
        </button>
        <button type="button" onClick={() => onConfirm(target)} style={{ flex: 1, appearance: "none", cursor: "pointer", height: 46, borderRadius: 12, background: "var(--color-loss)", border: "none", color: "#fff", fontSize: 14, fontWeight: 600 }}>
          Cancel order
        </button>
      </div>
    </Sheet>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 12, padding: "40px 16px", textAlign: "center" }}>
      <div style={{ fontSize: 13, color: "var(--fg-3)" }}>{text}</div>
    </div>
  );
}

export default function MobileOrders({
  orders = [],
  trades = [],
  positions = [],
  activeTab = "Positions",
  onTab,
  dateRange = "1W",
  onDateRange,
  onCancel,
  onRefresh,
}) {
  const [cancelTarget, setCancelTarget] = useState(null);
  const handleRefresh = onRefresh || (() => new Promise((res) => setTimeout(res, 600)));

  const count = activeTab === "Positions" ? positions.length : activeTab === "Trades" ? trades.length : orders.length;
  const noun = activeTab.toLowerCase();

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div style={{ padding: "0 16px", color: "var(--fg-1)" }}>
        <div style={{ paddingTop: 2, marginBottom: 12 }}>
          <Segmented options={TABS} value={activeTab} onChange={onTab} grow />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: "var(--fg-3)" }}>{count} {noun}</span>
          {/* range applies to Trades/Orders (windowed); Positions are live/current */}
          {activeTab !== "Positions" && <Segmented options={RANGES} value={dateRange} onChange={onDateRange} />}
        </div>

        {count === 0 ? (
          <EmptyState text={`No ${noun} in this range.`} />
        ) : (
          <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {activeTab === "Positions" &&
              positions.map((p, i) => <PositionRow key={p.strategy_instance_id || p.instrument_id || i} p={p} last={i === positions.length - 1} />)}
            {activeTab === "Trades" &&
              trades.map((t, i) => <TradeRow key={t.id || t.tradeId || i} t={t} last={i === trades.length - 1} />)}
            {activeTab === "Orders" &&
              orders.map((o, i) => (
                <OrderRow key={o.order_id || o.id || i} o={o} last={i === orders.length - 1} onCancelRequest={setCancelTarget} />
              ))}
          </div>
        )}

        {activeTab === "Orders" && orders.some((o) => CANCELLABLE.has((o.status || "").toUpperCase())) && (
          <div style={{ textAlign: "center", marginTop: 14, fontSize: 10, color: "var(--fg-3)", letterSpacing: ".03em" }}>
            Swipe a pending order left to Cancel
          </div>
        )}

        <CancelConfirmSheet target={cancelTarget} onCancel={() => setCancelTarget(null)} onConfirm={(o) => { onCancel?.(o); setCancelTarget(null); }} />
      </div>
    </PullToRefresh>
  );
}
